<template>
  <v-container fluid :class="mobile ? 'pa-2' : 'pa-4'" class="fill-height align-start">
    <v-row class="justify-center">
      <v-col cols="12" sm="10" md="8">
        <!-- Welcome title -->
        <h2 class="text-h5 text-center mb-6 mt-4">Welcome to Pizza Felice</h2>

        <!-- Service cards grid -->

        <!--
  d-flex is a Vuetify utility class that applies display: flex to the element, making it a flexbox container.

  By default, v-col is display: block — its children just stack and have independent heights. With d-flex, the column becomes a flex container, which means:
  - Children can use flex-grow-1 to stretch and fill available space
  - Two v-cols in the same v-row are already equal height (flexbox row), but their content isn't — the cards inside stop at their natural height
  - Adding d-flex to the v-col passes that "stretch to fill" behavior down one level, so the card inside also fills the full column height

  In short: v-row makes columns equal height, d-flex on v-col makes the cards inside also equal height.
  -->

        <v-row>
          <v-col
            v-for="service in visibleServices"
            :key="service.app"
            cols="12"
            sm="6"
            class="d-flex"
          >
            <v-card
              class="service-card flex-grow-1"
              variant="outlined"
              @click="navigateTo(service)"
              hover
            >
              <v-card-item>
                <template v-slot:prepend>
                  <v-icon :icon="service.icon" size="32" color="indigo" />
                </template>
                <v-card-title>{{ service.title }}</v-card-title>
                <v-card-subtitle v-if="service.subtitle">{{ service.subtitle }}</v-card-subtitle>
              </v-card-item>
              <v-card-text class="text-caption text-medium-emphasis pt-0">{{
                isAdmin ? service.adminDesc : service.userDesc
              }}</v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Admin tools (appadmin only — Settings routes require appadmin role) -->
        <template v-if="user?.role === 'appadmin'">
          <v-divider class="my-6" />
          <h3 class="text-subtitle-1 text-medium-emphasis text-center mb-4">Administration</h3>
          <v-row>
            <v-col
              v-for="tool in adminTools"
              :key="tool.route"
              cols="12"
              sm="6"
              class="d-flex"
            >
              <v-card
                class="service-card flex-grow-1"
                variant="outlined"
                @click="router.push(tool.route)"
                hover
              >
                <v-card-item>
                  <template v-slot:prepend>
                    <v-icon :icon="tool.icon" size="32" color="deep-purple" />
                  </template>
                  <v-card-title>{{ tool.title }}</v-card-title>
                  <v-card-subtitle v-if="tool.subtitle">{{ tool.subtitle }}</v-card-subtitle>
                </v-card-item>
                <v-card-text class="text-caption text-medium-emphasis pt-0">{{ tool.desc }}</v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <!-- Footer note -->
        <p class="text-caption text-medium-emphasis text-center mt-6">
          You can only see services you have been granted access to.<template v-if="isAdmin">
            Settings, Audit Logs, and User Management are reserved to system
            administrators.</template
          >
        </p>
      </v-col>
    </v-row>
  </v-container>
</template>

<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useDisplay } from "vuetify";
import { useApp } from "@/composables/useApp.js";

const router = useRouter();
const { mobile } = useDisplay();
const { user, currentApp, isAdmin } = useApp();

// All available services with role-specific descriptions
const services = [
  {
    app: ["accounting", "sales", "pettycash"],
    title: "Accounting",
    subtitle: "Sales · Inventory · Petty Cash · General Ledger",
    icon: "mdi-calculator-variant",
    route: "/accounting",
    adminDesc:
      "Track sales, import order data, manage petty cash transactions, and view analytics",
    userDesc: "View sales reports, petty cash transactions, and analytics",
  },
  {
    app: ["marketing", "reviews", "campaigns"],
    title: "Marketing",
    subtitle: "Campaigns · Reviews · Disputes",
    icon: "mdi-bullhorn",
    route: "/marketing",
    adminDesc:
      "Manage campaigns, promotions, and customer review assignments across delivery platforms",
    userDesc: "View campaigns and your daily review assignments",
  },
  {
    app: "assets",
    title: "Assets",
    subtitle: "Photos, videos and documents",
    icon: "mdi-folder-multiple-image",
    route: "/assets",
    adminDesc:
      "Upload, organize, and share digital assets with your team",
    userDesc: "Browse and download shared photos, videos, and documents",
  },
  {
    app: "contacts",
    title: "Employees and Contacts",
    subtitle: "HR · Address Book",
    icon: "mdi-account-box",
    route: "/hr",
    adminDesc:
      "Manage your employees records, and an address book of contacts, with notes, tags, and credentials",
    userDesc: "Look up contact details, credentials, and notes for suppliers, partners, and vendors",
  },
  {
    app: "menu",
    title: "Menu",
    subtitle: "Ingredients, recipes and food cost",
    icon: "mdi-silverware-fork-knife",
    route: "/menu",
    adminDesc:
      "Track ingredient prices, manage recipes with cost breakdowns, and monitor food cost percentages",
    userDesc: "View ingredient prices, recipe costs, and food cost analysis",
  },
  {
    app: "_docs",
    title: "Documentation",
    subtitle: "API reference and guides",
    icon: "mdi-book-open-variant",
    route: "/settings/docs",
    adminDesc: "API docs, architecture overview, testing guide",
    userDesc: "Architecture docs and user guides",
  },
];

// Admin-only tools shown below the service cards
const adminTools = [
  { title: "Audit Logs", subtitle: "Activity history", icon: "mdi-history", route: "/settings/audit-logs", desc: "View the system audit trail of user actions" },
  { title: "User Management", subtitle: "Accounts and roles", icon: "mdi-account-group", route: "/settings/users", desc: "Invite, edit, suspend, and manage user accounts" },
  { title: "System Settings", subtitle: "System configuration", icon: "mdi-cog", route: "/settings", desc: "Sales mappings, business rules, platform formats, and tags" },
];

// Helper to parse allowed_apps (handles both string and array formats)
const getAllowedApps = () => {
  const apps = user.value?.allowed_apps;
  if (!apps) return [];
  if (Array.isArray(apps)) return apps;
  try {
    return JSON.parse(apps);
  } catch {
    return [];
  }
};

// Show services the user has access to
// appadmin sees all services; app can be a string or array (user needs at least one match)
// _docs visible to appadmin and pettycash users (guides are permission-gated on the page)
const visibleServices = computed(() => {
  if (user.value?.role === "appadmin") return services;
  const apps = getAllowedApps();
  return services.filter((s) => {
    if (s.app === "_docs") return true;
    if (Array.isArray(s.app)) return s.app.some((a) => apps.includes(a));
    return apps.includes(s.app);
  });
});

// Navigate to service and set currentApp
const navigateTo = (service) => {
  currentApp.value = service.app;
  localStorage.setItem("currentApp", JSON.stringify(service.app));
  router.push(service.route);
};
</script>

<style scoped>
.service-card {
  cursor: pointer;
  transition: border-color 0.2s;
}
.service-card:hover {
  border-color: rgb(var(--v-theme-primary));
}
</style>
